import { type ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { HIGHLIGHT_LOGO_SIZES } from '../../highlight-logo/nested/sizes'

/**
 * The square follows `HighlightLogo.Tile`'s ramp (`HIGHLIGHT_LOGO_SIZES`), so a well and a tile of one size share
 * a box and a corner. The mark inside steps with the icon sizes rather than taking half the box like
 * the tile's symbol: the tile is an app icon, the well only frames someone else's mark.
 */
const WELL_MARK = {
  xs: '[&_:is(svg,img):not([class*="size-"])]:size-3',
  sm: '[&_:is(svg,img):not([class*="size-"])]:size-3.5',
  md: '[&_:is(svg,img):not([class*="size-"])]:size-4',
  lg: '[&_:is(svg,img):not([class*="size-"])]:size-5',
} as const

type LogoWellSize = keyof typeof WELL_MARK

/** The well's surface, shared with `Cell.Icon` so a row's badge and a tile's mark sit in the same square. */
export const LOGO_WELL_CLASS = 'bg-element corner-squircle inline-flex shrink-0 items-center justify-center'

interface LogoWellProps extends ComponentProps<'span'> {
  /** The square, from the tile ramp: 24, 32, 40 or 56px, with marks of 12, 14, 16 and 20. */
  size?: LogoWellSize
}

/**
 * The square a mark sits in on an integration tile or an onboarding card. Sizes an unsized `Logo`
 * (or icon) inside it, so a mark never fills the box and reads as a sticker. A tinted fill, not a
 * shadow: the well is inline.
 */
export function LogoWell({ size = 'md', className, ...props }: LogoWellProps) {
  const { box, radius } = HIGHLIGHT_LOGO_SIZES[size]
  return <span className={twMerge(LOGO_WELL_CLASS, box, radius, WELL_MARK[size], className)} {...props} />
}

export type { LogoWellSize }
